import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authFetch } from '../lib/authFetch';
import { errorMessage, readJson } from '../lib/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    if (!token) {
      setError('Missing reset token — use the link from your email');
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await readJson(res);
      if (!res.ok) {
        setError(errorMessage(res, data));
        return;
      }
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch {
      setError('Network error — please try again');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .rp-input {
          width: 100%;
          background: #111;
          border: 1px solid rgba(255,255,255,0.1);
          color: #e5e5e5;
          font-family: 'Inter', sans-serif;
          font-size: 0.9rem;
          padding: 12px 14px;
          outline: none;
          transition: border-color 0.18s;
          box-sizing: border-box;
        }
        .rp-input:focus { border-color: rgba(232,160,32,0.6); }
        .rp-input::placeholder { color: #4a4a4a; }
        .rp-btn {
          width: 100%;
          background: #E8A020;
          color: #000;
          font-family: 'Courier Prime', monospace;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.16em;
          font-size: 0.72rem;
          padding: 14px;
          border: none;
          cursor: pointer;
          transition: background 0.18s, box-shadow 0.18s;
          box-shadow: 0 0 24px rgba(232,160,32,0.3);
        }
        .rp-btn:hover:not(:disabled) { background: #f0b030; box-shadow: 0 0 40px rgba(232,160,32,0.55); }
        .rp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .rp-back {
          background: none;
          border: none;
          color: #606060;
          font-family: 'Courier Prime', monospace;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          cursor: pointer;
          transition: color 0.18s;
          padding: 0;
        }
        .rp-back:hover { color: #E8A020; }
        .rp-strength { height: 3px; background: #1a1a1a; margin-top: 6px; }
        .rp-strength-bar { height: 100%; transition: width 0.25s, background 0.25s; }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#0A0A0A', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <div style={{ position: 'fixed', inset: 0, background: 'radial-gradient(ellipse 70% 50% at 50% 40%, rgba(232,160,32,0.06) 0%, transparent 65%)', pointerEvents: 'none', zIndex: 0 }} />

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '400px' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <button className="rp-back" onClick={() => navigate('/login')} style={{ marginBottom: '24px', display: 'inline-block' }}>
              ← Back to Login
            </button>
            <div style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.4rem', color: '#E8A020', letterSpacing: '0.14em' }}>
              PANGAJI
            </div>
            <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.3em', color: '#606060', marginTop: '6px' }}>
              The Story Engine
            </p>
          </div>

          <div style={{ background: '#0f0f0f', border: '1px solid rgba(255,255,255,0.07)', padding: '40px 36px' }}>
            {!token ? (
              <div style={{ textAlign: 'center' }}>
                <p style={{ fontFamily: 'Courier Prime, monospace', fontSize: '1rem', color: '#f87171', marginBottom: '16px' }}>
                  Invalid reset link
                </p>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#606060', lineHeight: 1.6 }}>
                  This link is missing a token. Use the link from your reset email, or{' '}
                  <button
                    onClick={() => navigate('/forgot-password')}
                    style={{ background: 'none', border: 'none', color: '#E8A020', cursor: 'pointer', fontFamily: 'inherit', fontSize: 'inherit', padding: 0, textDecoration: 'underline' }}
                  >
                    request a new one
                  </button>.
                </p>
              </div>
            ) : success ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Courier Prime, monospace', fontSize: '1.1rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#E8A020', marginBottom: '16px' }}>
                  Password updated
                </div>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', color: '#808080', lineHeight: 1.7 }}>
                  Your password has been changed. Redirecting you to login…
                </p>
              </div>
            ) : (
              <>
                <h1 style={{ fontFamily: 'Courier Prime, monospace', fontWeight: 700, fontSize: '1.1rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '10px', color: '#fff' }}>
                  Set new password
                </h1>
                <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: '#606060', marginBottom: '24px', lineHeight: 1.6 }}>
                  Choose a strong password for your Pangaji account.
                </p>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080', marginBottom: '6px' }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      className="rp-input"
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="new-password"
                      autoFocus
                    />
                    {/* Minimal strength bar */}
                    <div className="rp-strength">
                      <div className="rp-strength-bar" style={{
                        width: password.length === 0 ? '0%' : password.length < 6 ? '25%' : password.length < 10 ? '60%' : '100%',
                        background: password.length === 0 ? 'transparent' : password.length < 6 ? '#f87171' : password.length < 10 ? '#E8A020' : '#4ade80',
                      }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: '#808080', marginBottom: '6px' }}>
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="rp-input"
                      placeholder="Repeat your password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      autoComplete="new-password"
                      style={{ borderColor: confirm && confirm !== password ? 'rgba(248,113,113,0.5)' : undefined }}
                    />
                  </div>

                  {error && (
                    <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#f87171', margin: 0, lineHeight: 1.5 }}>
                      {error}
                    </p>
                  )}

                  <button type="submit" className="rp-btn" disabled={loading} style={{ marginTop: '8px' }}>
                    {loading ? 'Updating…' : 'Update password'}
                  </button>
                </form>
              </>
            )}
          </div>

          <p style={{ textAlign: 'center', fontFamily: 'Courier Prime, monospace', fontSize: '0.62rem', color: '#404040', marginTop: '24px', letterSpacing: '0.05em' }}>
            Made in Nairobi 🇰🇪
          </p>
        </div>
      </div>
    </>
  );
}
